/**
 * briefingsMarkdownTransform — client-side mirror of the shared
 * promotional-text corrections applied by `api/auth/src/getBriefings.js`
 * (`stripPremierTease` + the new compact `substituteSoWhat` marker).
 *
 * SCOPE — SHARED PROMOTIONAL CHROME ONLY.
 * These transforms operate on the *shared promotional wrap* that surrounds
 * briefing content across every date:
 *
 *   • The bolded `**Premier subscribers see ...**` upsell sentences the
 *     composer baked into every "so what" branch and the hook paragraph.
 *   • The four-per-briefing "**So what →** _Premier subscribers see the
 *     trade-level impact here: which of your open positions just got
 *     mispriced... [Start Premier ($29.99/mo)](/register)_" teaser
 *     paragraph the earlier Lambda emitted for anonymous readers.
 *
 * They DO NOT touch:
 *
 *   • Original editorial content — headlines, data narratives, price
 *     tables, regime observations, or the Premier "So what:" text a
 *     signed-in Premier reader receives from the server for a given
 *     briefing date.
 *   • Anything date-specific to a particular briefing's analysis.
 *
 * Purpose: let a reviewer preview the corrected shared promotional copy
 * against production DDB rows before the corresponding Lambda change is
 * deployed. Once the Lambda ships, the incoming `publicMarkdown` is
 * already clean, and these transforms become idempotent no-ops (no
 * matches → no changes → `corrected === false`).
 *
 * Historical preservation: because analytical content is untouched, the
 * only visible correction is the site chrome. The consumer of this module
 * (N2BriefingPage) surfaces a small editorial-note label whenever
 * `corrected === true` so readers can distinguish a shared-copy update
 * from a silent editorial rewrite.
 */

/** Matches every `**Premier subscribers see ...**` / `... get ...**` bold
 * sentence the composer historically baked into the shared shell.
 * Case-insensitive on the leading word; non-greedy on the interior so
 * neighboring editorial `**bold**` isn't swallowed. */
const PREMIER_TEASE_RE = /\s*\*\*Premier subscribers?\b[^*]*?\*\*/gi;

/** Matches the pre-correction anonymous "So what" teaser paragraph the
 * old Lambda substituted for each `{{SO_WHAT:key}}` token. The paragraph
 * always started with `**So what →** _Premier subscribers see the trade-
 * level impact here:` and terminated at the closing italic underscore.
 * The `.` inside `[\s\S]` matches newlines (the paragraph can wrap). */
const LEGACY_SO_WHAT_TEASER_RE =
  /\*\*So what →\*\* _Premier subscribers see the trade-level impact here:[\s\S]*?_/g;

/** Replacement compact marker matching the new Lambda output. */
const COMPACT_SO_WHAT_MARKER = '**So what →** _Cross-asset analysis for this section is Premier._';

export interface CorrectionResult {
  /** The corrected markdown. */
  md: string;
  /** True when at least one shared-copy pattern was rewritten. */
  corrected: boolean;
}

/**
 * Apply the shared-copy corrections. Idempotent: running on already
 * corrected markdown returns identical text with `corrected === false`.
 *
 * @param md  raw `publicMarkdown` from the getBriefings response
 */
export function applyClientSideCorrections(md: string | null | undefined): CorrectionResult {
  if (!md) return { md: md ?? '', corrected: false };

  let out = md;
  let corrected = false;

  if (PREMIER_TEASE_RE.test(out)) {
    corrected = true;
    out = out.replace(PREMIER_TEASE_RE, '');
  }
  PREMIER_TEASE_RE.lastIndex = 0;

  if (LEGACY_SO_WHAT_TEASER_RE.test(out)) {
    corrected = true;
    out = out.replace(LEGACY_SO_WHAT_TEASER_RE, COMPACT_SO_WHAT_MARKER);
  }
  LEGACY_SO_WHAT_TEASER_RE.lastIndex = 0;

  // Collapse any run of two-or-more blank lines the strip left behind
  // (visual cleanup only; no editorial change).
  if (corrected) {
    out = out.replace(/\n{3,}/g, '\n\n').replace(/ +\n/g, '\n');
  }
  return { md: out, corrected };
}
